import { createTheme, ThemeProvider } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import AttorneyHubLogo from "_shared/_views/assets/attorney-hub-logo.svg";
import AdministratorInvitationAcceptanceForm from "legal-entities/invitations/administrator-invitations/_views/forms/administrator-invitation-acceptance-form";
import AdministratorInvitation from "legal-entities/invitations/administrator-invitations/administrator-invitation";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { useLazyGetAdministratorInvitationByIdQuery } from "../_api/administrator-invitation-api";

const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "#404558",
  boxSizing: "border-box",
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(5, 1),
  textAlign: "center",
  width: "100%",
}));
const Logo = styled("img")(({ theme }) => ({
  marginBottom: theme.spacing(4),
  maxWidth: "90%",
}));
const InvitationForm = styled(Container)(({ theme }) => ({
  color: theme.palette.common.white,
  padding: theme.spacing(2),
  position: "relative",
  width: "100%",
}));
const InvitationFormTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));
const InvitationFormInstructions = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& a": {
    color: theme.palette.common.white,
    textDecoration: "underline",
  },
}));

type OfficerInvitationPageProps = {
  className?: string;
  invitationId?: Guid;
};

export default function AdministratorInvitationPage(props: Readonly<OfficerInvitationPageProps>) {
  const { className } = props;

  const [invitation, setInvitation] = useState<AdministratorInvitation>();
  const [loading, setLoading] = useState<boolean>(false);
  const params = useParams<{ invitationId: string }>();
  const theme = createTheme();

  const [getAdministratorInvitationById] = useLazyGetAdministratorInvitationByIdQuery();

  const loadInvitation = useCallback(async () => {
    setLoading(true);

    try {
      let invitationId: Guid | undefined = undefined;

      if (props.invitationId) invitationId = props.invitationId;
      else if (params.invitationId) invitationId = new Guid(params.invitationId);

      if (!invitationId) throw new Error("Invitation ID was not provided");
      const returnedInvitation = AdministratorInvitation.fromJSON(
        await getAdministratorInvitationById(invitationId).unwrap(),
      );
      if (!returnedInvitation) throw new Error("Invitation not found");
      setInvitation(returnedInvitation);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [params.invitationId, props.invitationId, getAdministratorInvitationById]);

  useEffect(() => {
    void loadInvitation();
  }, [loadInvitation]);

  function invitationNotFoundMessage() {
    return (
      <InvitationForm className={className}>
        <InvitationFormTitle variant="h4">Invitation Not Found</InvitationFormTitle>
        <InvitationFormInstructions>
          The invitation couldn&apos;t be found.
          <br />
          If you believe this is in error please contact{" "}
          <a href="mailto:support@attorneyhub.com" target="_blank" rel="noreferrer">
            support@attorneyhub
          </a>
          .<br />
          You can close this tab
        </InvitationFormInstructions>
      </InvitationForm>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <FormContainer className={className}>
        <Logo src={AttorneyHubLogo} alt="AttorneyHub Logo" />
        {loading && <Loader />}
        {!invitation && !loading && invitationNotFoundMessage()}
        {invitation && <AdministratorInvitationAcceptanceForm invitation={invitation} />}
      </FormContainer>
    </ThemeProvider>
  );
}
